import React from 'react';
import jwt_decode from 'jwt-decode';
import useAuth from '../hooks/useAuth';
import {
  UserClient,
  AccountClient,
  TradeClient,
  BrokerClient,
  BillingClient,
  AnalyticsClient,
  NewsClient,
  NotificationClient,
  SentimentClient,
  StatusClient,
  AnalyzerServiceClient
} from './client';

const hasPermission = (token: string) => {
  const decoded: any = jwt_decode(token);
  if (decoded.permissions.length > 0) {
    return true;
  } else {
    return false;
  }
};

const tokenNotExpired = (token: string) => {
  if (token.length > 0) {
    const decoded: any = jwt_decode(token);
    const expireTime = new Date(decoded.exp * 1000);
    if (expireTime < new Date()) {
      return false;
    }
    return true;
  }
  return false;
};

export const useUserClient = (): (() => Promise<UserClient>) => {
  const { accessToken, refreshToken } = useAuth();
  const client = React.useRef(new UserClient());
  return React.useCallback(() => {
    return new Promise<UserClient>(async (resolve, reject) => {
      if (hasPermission(accessToken || '') && tokenNotExpired(accessToken || '')) {
        client.current.setMeta('Authorization', `Bearer ${accessToken}`);
        client.current.setMeta('Cache-Control', 'no-store');
        resolve(client.current);
      } else {
        refreshToken()
          .then((token) => {
            client.current.setMeta('Authorization', `Bearer ${token}`);
            client.current.setMeta('Cache-Control', 'no-store');
            resolve(client.current);
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  }, [accessToken, refreshToken]);
};

export const useAccountClient = (): (() => Promise<AccountClient>) => {
  const { accessToken, refreshToken } = useAuth();
  const client = React.useRef(new AccountClient());
  return React.useCallback(() => {
    return new Promise<AccountClient>(async (resolve, reject) => {
      if (hasPermission(accessToken || '') && tokenNotExpired(accessToken || '')) {
        client.current.setMeta('Authorization', `Bearer ${accessToken}`);
        client.current.setMeta('Cache-Control', 'no-store');
        resolve(client.current);
      } else {
        refreshToken()
          .then((token) => {
            client.current.setMeta('Authorization', `Bearer ${token}`);
            resolve(client.current);
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  }, [accessToken, refreshToken]);
};

export const useTradeClient = (): (() => Promise<TradeClient>) => {
  const { accessToken, refreshToken } = useAuth();
  const client = React.useRef(new TradeClient());
  return React.useCallback(() => {
    return new Promise<TradeClient>(async (resolve, reject) => {
      if (hasPermission(accessToken || '') && tokenNotExpired(accessToken || '')) {
        client.current.setMeta('Authorization', `Bearer ${accessToken}`);
        resolve(client.current);
      } else {
        refreshToken()
          .then((token) => {
            client.current.setMeta('Authorization', `Bearer ${token}`);
            resolve(client.current);
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  }, [accessToken, refreshToken]);
};

export const useBrokerClient = (): (() => Promise<BrokerClient>) => {
  const { accessToken, refreshToken } = useAuth();
  const client = React.useRef(new BrokerClient());
  return React.useCallback(() => {
    return new Promise<BrokerClient>(async (resolve, reject) => {
      if (hasPermission(accessToken || '') && tokenNotExpired(accessToken || '')) {
        client.current.setMeta('Authorization', `Bearer ${accessToken}`);
        resolve(client.current);
      } else {
        refreshToken()
          .then((token) => {
            client.current.setMeta('Authorization', `Bearer ${token}`);
            resolve(client.current);
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  }, [accessToken, refreshToken]);
};

export const useBillingClient = (): (() => Promise<BillingClient>) => {
  const { accessToken, refreshToken } = useAuth();
  const client = React.useRef(new BillingClient());
  return React.useCallback(() => {
    return new Promise<BillingClient>(async (resolve, reject) => {
      if (hasPermission(accessToken || '') && tokenNotExpired(accessToken || '')) {
        client.current.setMeta('Authorization', `Bearer ${accessToken}`);
        resolve(client.current);
      } else {
        refreshToken()
          .then((token) => {
            client.current.setMeta('Authorization', `Bearer ${token}`);
            resolve(client.current);
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  }, [accessToken, refreshToken]);
};

export const useAnalyticsClient = (): (() => Promise<AnalyticsClient>) => {
  const { accessToken, refreshToken } = useAuth();
  const client = React.useRef(new AnalyticsClient());
  return React.useCallback(() => {
    return new Promise<AnalyticsClient>(async (resolve, reject) => {
      if (hasPermission(accessToken || '') && tokenNotExpired(accessToken || '')) {
        client.current.setMeta('Authorization', `Bearer ${accessToken}`);
        resolve(client.current);
      } else {
        refreshToken()
          .then((token) => {
            client.current.setMeta('Authorization', `Bearer ${token}`);
            resolve(client.current);
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  }, [accessToken, refreshToken]);
};

export const useNewsClient = (): (() => Promise<NewsClient>) => {
  const { accessToken, refreshToken } = useAuth();
  const client = React.useRef(new NewsClient());
  return React.useCallback(() => {
    return new Promise<NewsClient>(async (resolve, reject) => {
      if (hasPermission(accessToken || '') && tokenNotExpired(accessToken || '')) {
        client.current.setMeta('Authorization', `Bearer ${accessToken}`);
        resolve(client.current);
      } else {
        refreshToken()
          .then((token) => {
            client.current.setMeta('Authorization', `Bearer ${token}`);
            resolve(client.current);
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  }, [accessToken, refreshToken]);
};

export const useNotificationClient = (): (() => Promise<NotificationClient>) => {
  const { accessToken, refreshToken } = useAuth();
  const client = React.useRef(new NotificationClient());
  return React.useCallback(() => {
    return new Promise<NotificationClient>(async (resolve, reject) => {
      if (hasPermission(accessToken || '') && tokenNotExpired(accessToken || '')) {
        client.current.setMeta('Authorization', `Bearer ${accessToken}`);
        resolve(client.current);
      } else {
        refreshToken()
          .then((token) => {
            client.current.setMeta('Authorization', `Bearer ${token}`);
            resolve(client.current);
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  }, [accessToken, refreshToken]);
};

export const useSentimentClient = (): (() => Promise<SentimentClient>) => {
  const { accessToken, refreshToken } = useAuth();
  const client = React.useRef(new SentimentClient());

  return React.useCallback(() => {
    return new Promise<SentimentClient>(async (resolve, reject) => {
      if (hasPermission(accessToken || '') && tokenNotExpired(accessToken || '')) {
        client.current.setMeta('Authorization', `Bearer ${accessToken}`);
        resolve(client.current);
      } else {
        refreshToken()
          .then((token) => {
            client.current.setMeta('Authorization', `Bearer ${token}`);
            resolve(client.current);
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  }, [accessToken, refreshToken]);
};

export const useStatusClient = (): (() => Promise<StatusClient>) => {
  const { accessToken, refreshToken } = useAuth();
  const client = React.useRef(new StatusClient());

  return React.useCallback(() => {
    return new Promise<StatusClient>(async (resolve, reject) => {
      if (hasPermission(accessToken || '') && tokenNotExpired(accessToken || '')) {
        client.current.setMeta('Authorization', `Bearer ${accessToken}`);
        resolve(client.current);
      } else {
        refreshToken()
          .then((token) => {
            client.current.setMeta('Authorization', `Bearer ${token}`);
            resolve(client.current);
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  }, [accessToken, refreshToken]);
};

export const useAnalyzerClient = (): (() => Promise<AnalyzerServiceClient>) => {
  const { accessToken, refreshToken } = useAuth();
  const client = React.useRef(new AnalyzerServiceClient());
  return React.useCallback(() => {
    return new Promise<AnalyzerServiceClient>(async (resolve, reject) => {
      if (hasPermission(accessToken || '') && tokenNotExpired(accessToken || '')) {
        client.current.setMeta('Authorization', `Bearer ${accessToken}`);
        resolve(client.current);
      } else {
        refreshToken()
          .then((token) => {
            client.current.setMeta('Authorization', `Bearer ${token}`);
            resolve(client.current);
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  }, [accessToken, refreshToken]);
};
